import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, RefreshCw, ChevronDown } from 'lucide-react';
import { API } from '@/App';

/**
 * Update banner shown to every authenticated user after a redeployment.
 *
 * - Visible top of the screen (under any impersonation banner).
 * - Includes "Mettre à jour" button that hard-refreshes the SPA + clears caches.
 * - Two states:
 *    - Compact (default): single line CTA
 *    - Expanded: shows release notes (highlights/fixes)
 * - Dismissible per-user (server-side) via /admin/deployment-info/seen.
 */
const UpdateAvailableBanner = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [info, setInfo] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/admin/deployment-info`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setInfo(r.data))
      .catch(() => {});
  }, []);

  const dismiss = async () => {
    const token = localStorage.getItem('token');
    setDismissed(true);
    localStorage.setItem('pwa-update-done', String(Date.now()));
    try {
      await axios.post(`${API}/admin/deployment-info/seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* silent */ }
  };

  const hardReload = async () => {
    const token = localStorage.getItem('token');
    setDismissed(true);
    localStorage.setItem('pwa-update-done', String(Date.now()));
    try {
      await axios.post(`${API}/admin/deployment-info/seen`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch { /* silent */ }
    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
    } catch { /* silent */ }
    window.location.reload();
  };

  const lastUpdate = parseInt(localStorage.getItem('pwa-update-done') || '0', 10);
  const inCooldown = Date.now() - lastUpdate < 3600000;
  if (!info?.has_new || dismissed || inCooldown) return null;
  const isImpersonating = localStorage.getItem('impersonating') === 'true';
  const highlights = info.release_notes?.highlights || [];

  return (
    <div className="fixed left-0 right-0 z-[9990] px-3 sm:px-4 pointer-events-none"
      style={{ top: (isImpersonating ? 40 : 12) + (pathname.startsWith('/admin') ? 56 : 0) }}
      data-testid="update-available-banner">
      <div className="pointer-events-auto max-w-3xl mx-auto rounded-2xl bg-gradient-to-r from-[#1e3a5f] via-[#2a4a78] to-[#35AFE4] text-white shadow-2xl shadow-[#1e3a5f]/30 p-3 sm:p-4 backdrop-blur">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/15 flex items-center justify-center">
            <Sparkles size={16} className="text-white animate-pulse" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-bold">Nouvelle version disponible</p>
              {info.version && <span className="px-2 py-0.5 rounded-full bg-white text-[#1e3a5f] text-[10px] font-bold tracking-wider">v{info.version}</span>}
            </div>
            <p className="text-[12px] text-white/85 mt-0.5">
              Cliquez sur <strong>Mettre à jour</strong> pour rafraîchir l'application avec les dernières améliorations.
            </p>
            {highlights.length > 0 && (
              <button onClick={() => setExpanded(!expanded)}
                className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold text-white/90 hover:text-white"
                data-testid="update-toggle-notes">
                {expanded ? 'Masquer' : `Voir les nouveautés (${highlights.length})`}
                <ChevronDown size={12} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </button>
            )}
            {expanded && highlights.length > 0 && (
              <ul className="mt-2 space-y-1 text-[12px] text-white/90 pl-3" data-testid="update-notes-list">
                {highlights.slice(0, 6).map((h, i) => (
                  <li key={i} className="flex gap-2"><span className="text-white/60">•</span><span>{h}</span></li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <button onClick={hardReload}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-[#1e3a5f] text-xs font-bold rounded-full hover:bg-white/90 transition-colors shadow-sm"
              data-testid="update-banner-reload">
              <RefreshCw size={12} /> Mettre à jour
            </button>
            <button onClick={dismiss}
              className="text-[10px] text-white/70 hover:text-white px-2 py-0.5"
              data-testid="update-banner-dismiss">
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateAvailableBanner;
